import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from '@/api/ApiContext';
import type { ProductCreateRequest } from '@/api/handlers/resources/products/requests';
import type { GetProductDetailResponse } from '@/api/handlers/resources/products/responses';
import type { ApiResponse } from '@/api/handlers/responses';
import { ProductQueryKeys } from './productQueryKeys';

/**
 * Mutation behind the create-product form. On success it invalidates every
 * `/products` list cache (any filter/page) so the new product shows up, and
 * every Barcode issues page, which lists it when its barcode has letters
 * (review of #241, finding 8); the
 * caller navigates to the returned product's detail page. The query function
 * never rejects — the form branches on `result.success` and surfaces the
 * server's 400 `error` text inline.
 */
export const useCreateProductMutation = () => {
    const { apiClient } = useApi();
    const queryClient = useQueryClient();

    return useMutation<
        ApiResponse<GetProductDetailResponse>,
        Error,
        ProductCreateRequest
    >({
        mutationFn: (body) => apiClient.products.createProduct(body),
        onSuccess: (result) => {
            if (result.success) {
                queryClient.invalidateQueries({
                    queryKey: ProductQueryKeys.lists(),
                });
                queryClient.invalidateQueries({
                    queryKey: ProductQueryKeys.barcodeIssueLists(),
                });
            }
        },
    });
};
